import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import emptyImg from '../../../../extensions/images/empty-preview.png';
import { validateWidgetImage } from '../../../../helpers/validateWidgetImage';

const useStyles = makeStyles({
  root: {
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    columnGap: '16px',
    fontFamily: 'SF UI Display Light',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '4px',
  },
  preview: {
    width: '80px',
    height: '80px',
    flexShrink: 0,
    objectFit: 'cover',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '8px',
    backgroundColor: 'rgba(215, 215, 215, 0.23)',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  main: {
    flexGrow: 1,
    fontSize: '20px',
    fontWeight: 'bold',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    columnGap: '12px',
    flexShrink: 0,
    color: 'rgb(0, 123, 255)',
  },
  step: {
    fontSize: '20px',
    marginBottom: '4px',
  },
  name: {
    fontSize: '14px',
    minHeight: '16px',
  },
});

interface Props {
  image: string | null;
  title: string;
  onChange: ({ encodedImage, previewImage }: { encodedImage: string | null; previewImage: string | null }) => void;
  onError: (message: string) => void;
  onPreview: (image: string) => void;
  onDelete: () => void;
}

export default function WidgetImageItem({ image, title, onChange, onError, onPreview, onDelete }: Props) {
  const classes = useStyles();

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    try {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const { isValid, error } = validateWidgetImage(file);
      if (!isValid) {
        throw new Error(error);
      } else {
        const base64Image = await getBase64Image(file);
        const encodedImageData = encodeImageData(file, base64Image);
        onChange({ encodedImage: encodedImageData, previewImage: base64Image });
      }
    } catch (error: any) {
      onError(error.message);
    }
  };

  const handleDelete = () => {
    onDelete();
  };

  const handlePreview = () => {
    if (image !== null) {
      onPreview(image);
    }
  };

  return (
    <Box className={classes.root}>
      <img className={classes.preview} src={image || emptyImg} alt='preview' />
      <Box className={classes.content}>
        <Box className={classes.main}>
          <Box className={classes.step}>{title}</Box>
          {/* <Box className={classes.name}>{fileName}</Box> */}
        </Box>
        <Box className={classes.footer}>
          {Boolean(image) && (
            <IconButton color='inherit' size='small' onClick={handlePreview} aria-label='show'>
              <VisibilityIcon />
            </IconButton>
          )}
          <React.Fragment>
            <input
              id={`logo-icon__${title}`}
              className='inputForUploadFile'
              type='file'
              accept='image/*'
              onChange={handleChange}
            />
            <Box component='label' htmlFor={`logo-icon__${title}`}>
              <IconButton color='inherit' size='small' component='span' label='Upload Logo Image'>
                {image ? <EditIcon /> : <AddCircleIcon />}
              </IconButton>
            </Box>
          </React.Fragment>
          <IconButton color='secondary' size='small' onClick={handleDelete} aria-label='delete'>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

function getBase64Image(image: File) {
  return new Promise<string>((res, rej) => {
    const reader = new FileReader();
    reader.readAsDataURL(image);
    reader.onload = function (event) {
      const src = event && event.target && event.target.result;
      if (typeof src === 'string') {
        res(src);
      }
    };
    reader.onerror = function () {
      rej(new Error('An error occurred while reading the file'));
    };
  });
}

function encodeImageData(image: File, base64Image: string) {
  const imageData = {
    name: image.name,
    type: image.type,
    size: image.size,
    data: base64Image,
  };
  return JSON.stringify(imageData);
}
